import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './HeroCrowd.css';

const INK = '20,20,20';
// 群衆の色。OSUMITSUKIの各サービスロゴの色（オレンジ・青・緑）と墨を使う。
// エンジはOSUMITSUKIで実力が証明された人だけに使うので、ここには入れない
const CROWD_COLORS = ['20,20,20', '232,114,34', '31,78,140', '46,107,52'];
const ACCENT = '172,1,45';
const ACCENT_HEX = '#AC012D';

// 決まった並びを毎回再現するための簡単な乱数
function createRandom(seed) {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return s / 2147483647;
  };
}

// 1人分の上半身シルエット（頭・首・肩から下）のパスを描き、fill か stroke で塗る
function tracePerson(g, p, height, paint) {
  const { x, y, s, hair, broad } = p;
  const headR = 15 * s;
  g.beginPath();
  g.ellipse(x, y, headR * (hair ? 1.12 : 1), headR * (hair ? 1.18 : 1.08), 0, 0, Math.PI * 2);
  paint();
  if (hair) {
    g.beginPath();
    g.ellipse(x, y + headR * 0.9, headR * 1.05, headR * 0.9, 0, 0, Math.PI * 2);
    paint();
  }
  g.beginPath();
  g.rect(x - headR * 0.38, y + headR * 0.8, headR * 0.76, headR * 0.8);
  paint();
  const sw = headR * (broad ? 3.3 : 2.8);
  const top = y + headR * 1.45;
  g.beginPath();
  g.moveTo(x - sw, height + 10);
  g.bezierCurveTo(x - sw, top + headR * 1.2, x - sw * 0.8, top, x - headR * 0.5, top);
  g.lineTo(x + headR * 0.5, top);
  g.bezierCurveTo(x + sw * 0.8, top, x + sw, top + headR * 1.2, x + sw, height + 10);
  g.closePath();
  paint();
}

// 群衆の配置と、各点がどの人に属するかを計算する
function buildCrowd(width, height) {
  const narrow = width < 800;
  const random = createRandom(7);
  // 奥・中・手前の3列。手前ほど大きく、下に置く
  const rows = narrow
    ? [{ s: 1.15, y: 0.62, n: 4 }, { s: 1.5, y: 0.73, n: 3 }, { s: 1.9, y: 0.86, n: 3 }]
    : [{ s: 1.2, y: 0.3, n: 10 }, { s: 1.6, y: 0.48, n: 8 }, { s: 2.05, y: 0.68, n: 6 }];
  const people = [];
  rows.forEach((row, depth) => {
    for (let i = 0; i < row.n; i += 1) {
      const t = (i + 0.5 + (depth % 2 ? 0.5 : 0)) / row.n;
      people.push({
        x: width * (-0.04 + 1.08 * Math.min(t, 0.98)) + (random() - 0.5) * 30,
        y: height * row.y + (random() - 0.5) * 26,
        s: row.s * (0.9 + random() * 0.2),
        hair: random() < 0.45,
        broad: random() < 0.5,
        score: 58 + Math.floor(random() * 34),
        color: CROWD_COLORS[(people.length + Math.floor(random() * 3)) % CROWD_COLORS.length],
        star: false,
        depth,
      });
    }
  });
  // 何人かを OSUMITSUKI で実力が証明された人にする
  (narrow ? [2, 7] : [7, 14, 22]).forEach((i) => {
    if (people[i]) {
      people[i].star = true;
      people[i].score = 90 + (i % 9);
    }
  });

  // 人ごとに別の色で塗ったマスクを作る。塗ったあとに背景色で輪郭をなぞり、重なった人どうしの境目を出す
  const mask = document.createElement('canvas');
  mask.width = width;
  mask.height = height;
  const g = mask.getContext('2d', { willReadFrequently: true });
  g.lineJoin = 'round';
  people.forEach((p, i) => {
    const color = `rgb(${i + 1},0,0)`;
    g.fillStyle = color;
    tracePerson(g, p, height, () => g.fill());
    g.strokeStyle = 'rgb(0,0,0)';
    g.lineWidth = 5 * p.s;
    tracePerson(g, p, height, () => g.stroke());
    g.fillStyle = color;
    tracePerson(g, p, height, () => g.fill());
  });
  const pixels = g.getImageData(0, 0, width, height).data;
  const gap = narrow ? 6 : 7;
  const dots = [];
  for (let y = gap / 2; y < height; y += gap) {
    const offset = (Math.round(y / gap) % 2) * (gap / 2);
    for (let x = gap / 2 + offset; x < width; x += gap) {
      const id = pixels[(Math.floor(y) * width + Math.floor(x)) * 4];
      if (id > 0) dots.push({ x, y, p: id - 1 });
    }
  }
  return { people, dots, radius: narrow ? 70 : 96, narrow };
}

function drawFrame(ctx, crowd, ring, width, height) {
  const { people, dots, radius } = crowd;
  ctx.clearRect(0, 0, width, height);

  const distances = people.map((p) => Math.hypot(p.x - ring.x, p.y - ring.y));
  let found = -1;
  let near = -1;
  distances.forEach((d, i) => {
    if (people[i].star && d < radius * 0.75 && (found < 0 || d < distances[found])) found = i;
    // 人と人のすき間でも数字が消えないよう、円の少し外の人まで対象にする
    if (d < radius * 1.6 && (near < 0 || d < distances[near])) near = i;
  });

  for (const dot of dots) {
    const dist = Math.hypot(dot.x - ring.x, dot.y - ring.y);
    const p = people[dot.p];
    let alpha = [0.72, 0.86, 1][p.depth];
    let size = 2.7;
    let color = p.color;
    if (dot.p === found) {
      alpha = 1;
      size = 3.4;
      color = ACCENT;
    } else if (dist < radius) {
      const k = 1 - dist / radius;
      alpha = 1;
      size = 3 + k * 1;
    } else if (distances[dot.p] < radius) {
      alpha = Math.min(1, alpha + 0.12);
    } else if (p.star) {
      color = ACCENT;
      alpha = Math.max(alpha, 0.85);
    }
    ctx.fillStyle = `rgba(${color},${alpha})`;
    ctx.fillRect(dot.x - size / 2, dot.y - size / 2, size, size);
  }

  // 円の中心に一番近い人のスコアを、円の中に大きく表示する
  const shown = found >= 0 ? found : near;
  if (shown >= 0) {
    const star = shown === found;
    const size = Math.round(radius * 0.46);
    ctx.textAlign = 'center';
    ctx.lineJoin = 'round';
    const text = (str, font, y, color) => {
      ctx.font = font;
      ctx.lineWidth = 6;
      ctx.strokeStyle = 'rgba(255,255,255,.92)';
      ctx.strokeText(str, ring.x, y);
      ctx.fillStyle = color;
      ctx.fillText(str, ring.x, y);
    };
    const mono = '"IBM Plex Mono", ui-monospace, monospace';
    text('SCORE', `500 ${Math.round(size * 0.26)}px ${mono}`, ring.y - size * 0.62, star ? ACCENT_HEX : `rgba(${INK},.7)`);
    text(String(people[shown].score), `500 ${size}px ${mono}`, ring.y + size * 0.36, star ? ACCENT_HEX : `rgb(${INK})`);
    if (star) {
      text('OSUMITSUKI', `700 ${Math.round(size * 0.3)}px "Noto Sans JP", sans-serif`, ring.y + size * 0.82, ACCENT_HEX);
    }
    ctx.textAlign = 'start';
  }

  ctx.beginPath();
  ctx.strokeStyle = found >= 0 ? ACCENT_HEX : `rgba(${INK},.45)`;
  ctx.lineWidth = found >= 0 ? 2 : 1;
  ctx.arc(ring.x, ring.y, radius, 0, Math.PI * 2);
  ctx.stroke();
}

function HeroCrowd() {
  const stageRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const stage = stageRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const ring = { x: 0, y: 0 };
    const target = { x: 0, y: 0 };
    let crowd = null;
    let width = 0;
    let height = 0;
    let touched = -Infinity;
    let frame = 0;
    let visible = true;

    const build = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = stage.clientWidth;
      height = stage.clientHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      crowd = buildCrowd(width, height);
      ring.x = target.x = width * (crowd.narrow ? 0.5 : 0.7);
      ring.y = target.y = height * (crowd.narrow ? 0.73 : 0.45);
      drawFrame(ctx, crowd, ring, width, height);
    };

    const tick = (now) => {
      frame = 0;
      if (!visible || !crowd) return;
      // 触られていない間は、円がゆっくり群衆の中を探す
      if (!reduced && now - touched > 2500) {
        const t = now / 1000;
        target.x = width * (crowd.narrow ? 0.5 + 0.32 * Math.sin(t * 0.35) : 0.66 + 0.26 * Math.sin(t * 0.3));
        target.y = height * (crowd.narrow ? 0.75 + 0.07 * Math.sin(t * 0.7) : 0.45 + 0.15 * Math.sin(t * 0.55));
      }
      const k = reduced ? 1 : 0.09;
      ring.x += (target.x - ring.x) * k;
      ring.y += (target.y - ring.y) * k;
      drawFrame(ctx, crowd, ring, width, height);
      if (!reduced || now - touched < 100) frame = requestAnimationFrame(tick);
    };
    const start = () => {
      if (!frame) frame = requestAnimationFrame(tick);
    };

    const onPointerMove = (e) => {
      const rect = stage.getBoundingClientRect();
      target.x = e.clientX - rect.left;
      target.y = e.clientY - rect.top;
      touched = performance.now();
      start();
    };

    let resizeTimer;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(build, 150);
    };

    // 画面外に出たら描画を止める
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) start();
    });

    build();
    start();
    stage.addEventListener('pointermove', onPointerMove);
    window.addEventListener('resize', onResize);
    observer.observe(stage);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => crowd && drawFrame(ctx, crowd, ring, width, height));
    }

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(resizeTimer);
      stage.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('resize', onResize);
      observer.disconnect();
    };
  }, []);

  return (
    <section className="hero-crowd">
      <div className="hero-crowd-stage" ref={stageRef}>
        <canvas
          ref={canvasRef}
          className="hero-crowd-canvas"
          role="img"
          aria-label="点で描かれた大勢の人のシルエット。円でなぞった人だけが浮かび上がり、その中からOSUMITSUKIで実力が証明された人が見つかる"
        />
        <div className="hero-crowd-copy">
          <p className="hc-eyebrow">THE SOCIAL STANDARD FOR TALENT</p>
          <h1 className="hc-headline">
            その実力に、<br />
            <span className="hc-keep"><em>OSUMITSUKI</em>を。</span>
          </h1>
          <p className="hc-lead">
            肩書きや経歴の下にある、本当の実力。PortRayは共通の評価基準でそれを測り、社会で通用する確かな評価として証明します。
          </p>
          <div className="hc-actions">
            <Link to="/service" className="hc-btn hc-btn-primary">サービスを見る</Link>
            <Link to="/contact" className="hc-btn hc-btn-line">お問い合わせ</Link>
          </div>
          <dl className="hc-facts">
            <div>
              <dt>導入企業</dt>
              <dd>200<small>社+</small></dd>
            </div>
            <div>
              <dt>月間評価件数</dt>
              <dd>30<small>万件規模</small></dd>
            </div>
          </dl>
        </div>
      </div>
    </section>
  );
}

export default HeroCrowd;
